import * as React from 'react';

import { useQuery } from '@apollo/client';

import type { CollectionType } from 'bundles/enterprise-collections/components/CollectionItem';
import ForCreditCurriculumTracksByProgramQuery from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/ForCreditCurriculumTracksByProgram.graphql';
import type {
  ForCreditCurriculumTracksByProgramQuery as ForCreditCurriculumTracksByProgramQueryData,
  ForCreditCurriculumTracksByProgramQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/__generated__/ForCreditCurriculumTracksByProgram';
import type { Product as CurriculumProduct } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import EnterpriseForCreditCollectionContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseForCreditCollectionContext';
import useCourseEntitiesAsCurriculumCourses from 'bundles/page-config-common/hooks/useCourseEntitiesAsCurriculumCourses';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { ENTERPRISE_COLLECTION_TYPES } from 'bundles/unified-home-common/constants';

type Props = {
  children: React.ReactNode;
};

const EnterpriseForCreditCollectionProvider = ({ children }: Props) => {
  const program = useProgram();
  const programId = program?.id;

  const { data, loading } = useQuery<
    ForCreditCurriculumTracksByProgramQueryData,
    ForCreditCurriculumTracksByProgramQueryVariables
  >(ForCreditCurriculumTracksByProgramQuery, {
    variables: {
      programId: programId || '',
      allowOrgForSpecializationConfiguration: true,
    },
    context: { clientName: 'gatewayGql' },
    skip: !programId,
    ssr: false,
    errorPolicy: 'all' as const,
  });
  const allCourseIds =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.forCreditCurriculumTracksByProgram?.elements?.reduce(
      (acc, collection) => {
        if (collection?.courseIds?.length) {
          acc.push(...(collection.courseIds as string[]));
        }

        return acc;
      },
      [] as string[]
    );
  const [curriculumCourseMap, curriculumCourseMapLoading] = useCourseEntitiesAsCurriculumCourses(allCourseIds);

  if (!programId || loading || curriculumCourseMapLoading) {
    return (
      <EnterpriseForCreditCollectionContext.Provider
        value={{ forCreditCollectionLoading: true, forCreditCollection: undefined }}
      >
        {children}
      </EnterpriseForCreditCollectionContext.Provider>
    );
  }

  const forCreditCollectionData =
    data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.forCreditCurriculumTracksByProgram?.elements?.[0];

  const forCreditCollection = forCreditCollectionData
    ? ({
        ...forCreditCollectionData,
        courses: {
          elements: curriculumCourseMap
            ? (forCreditCollectionData?.courseIds?.map((id) => {
                if (curriculumCourseMap[id as string]) {
                  return curriculumCourseMap[id as string];
                } else {
                  return null;
                }
              }) as Array<CurriculumProduct | null> | null)
            : [],
        },
        enterpriseProductConfigurations: forCreditCollectionData?.programProductMetadata?.elements,
        // This is crticial so that we are able to render the proper collections in the config rendering logic.
        type: ENTERPRISE_COLLECTION_TYPES.FOR_CREDIT,
      } as CollectionType)
    : undefined;

  return (
    <EnterpriseForCreditCollectionContext.Provider value={{ forCreditCollectionLoading: false, forCreditCollection }}>
      {children}
    </EnterpriseForCreditCollectionContext.Provider>
  );
};

export default EnterpriseForCreditCollectionProvider;
